// src/pages/auth/AcceptEmployeeInvitation.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  User,
  Loader2,
  AlertCircle,
  Eye,
  EyeOff,
  CheckCircle,
  Shield,
} from "lucide-react";
import { toast } from "sonner";
import Cookies from "js-cookie";

import {
  verifyEmployeeInvite,
  acceptEmployeeInvite,
  acceptAdminInvite,
  setAuthHeader,
} from "@/services/authService";

const AcceptEmployeeInvitation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // ✅ detect admin accept link based on pathname
  const isAdminInvite = useMemo(() => {
    return (location?.pathname || "").includes("accept-admin-access");
  }, [location?.pathname]);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (token) verifyInvitation();
    else {
      setError("Invalid invitation link");
      setLoading(false);
    }
  }, [token, isAdminInvite]);

  const verifyInvitation = async () => {
    try {
      setLoading(true);

      const response = await verifyEmployeeInvite(token);

      setInvitation(response.data);
    } catch (error) {
      setError(
        error.response?.data?.detail || "Invalid or expired invitation link"
      );
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    if (!formData.password) return toast.error("Password is required");
    if (formData.password.length < 8)
      return toast.error("Password must be at least 8 characters");
    if (formData.password !== formData.confirmPassword)
      return toast.error("Passwords do not match");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    try {
      setSubmitting(true);

      // ✅ choose API based on mode
      const response = isAdminInvite
        ? await acceptAdminInvite(token, formData.password)
        : await acceptEmployeeInvite(token, formData.password);

      Cookies.set("user", JSON.stringify(response.data.user), {
        expires: 7,
        secure: window.location.protocol === "https:",
      });

      setAuthHeader(response.data.access_token);

      toast.success(
        isAdminInvite
          ? "Welcome — your Admin access is activated!"
          : "Welcome — your Employee Portal is ready!"
      );

      setTimeout(() => {
        navigate("/");
      }, 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="text-center p-6">
            <AlertCircle className="mx-auto h-10 w-10 text-red-500" />
            <p className="mt-4">{error}</p>
            <Button onClick={() => navigate("/")} className="mt-4">
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-lg">
        <CardHeader className="text-center">
          <div className="w-14 h-14 mx-auto bg-primary rounded-full flex items-center justify-center mb-3">
            {isAdminInvite ? (
              <Shield className="text-white h-7 w-7" />
            ) : (
              <User className="text-white h-7 w-7" />
            )}
          </div>

          <CardTitle>
            {isAdminInvite ? "Activate Your Admin Access" : "Activate Your Employee Portal"}
          </CardTitle>

          <p className="text-gray-500 mt-1">
            You have been invited to access{" "}
            {isAdminInvite ? "the admin panel" : "your employee account"} at
            <strong> {invitation?.company_name}</strong>.
          </p>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="bg-muted p-3 rounded">
              <p className="text-sm">
                <strong>Name:</strong> {invitation?.name}
              </p>
              <p className="text-sm">
                <strong>Email:</strong> {invitation?.email}
              </p>
            </div>

            <div>
              <Label>Create Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      password: e.target.value,
                    }))
                  }
                  placeholder="Minimum 8 characters"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <Label>Confirm Password</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      confirmPassword: e.target.value,
                    }))
                  }
                  placeholder="Re-enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-2.5"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            <Button disabled={submitting} className="w-full">
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating your account...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Activate Account
                </>
              )}
            </Button>
          </form>

          <div className="mt-4 text-sm text-gray-500">
            {isAdminInvite
              ? "Once activated, you will be able to manage users, roles, payroll and company settings from the Admin Portal."
              : "Once activated, you will be able to view payslips, profile, attendance and requests directly from your Employee Portal."}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptEmployeeInvitation;
